import {
    LoadingManager,
    CubeTextureLoader,
    Vector3,
    TextureLoader,
    Texture,
    Mesh,
    Group,
    Bone,
    Box3,
    BoxGeometry,
    BufferGeometry,
    Quaternion,
    Matrix4,
    CubeTexture,
    MeshStandardMaterial,
    SRGBColorSpace,
    DoubleSide,
    NearestFilter,
    Scene,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OBB } from "../assets/JS/OBB";
import { AssetType } from "./enums";
import { StaticMesh } from "./StaticMesh";
import * as modelsData from "assets/json/models.json";
import * as rooms from "assets/json/scenes.json";
import { Inventory } from "./InventoryService";
import { Robot } from "./Robot";
import { dataEvents } from "./Events";
import { IDataModel, ISceneConfiguration } from "./interfaces";
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from "three-mesh-bvh";
import { RiggedMesh } from "./RiggedMesh";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";

const PATIENT_COLOUR = "#999";
const BED_COLOUR = "#666";

export interface RiggedInventoryType {
    surgeon: RiggedMesh | null;
}

export const RiggedInventory: RiggedInventoryType = {
    surgeon: null,
};

export class LoaderService {
    loadingMan: LoadingManager;
    gltfLoader: GLTFLoader;
    envMapLoader: CubeTextureLoader;
    texLoader: TextureLoader;
    patientBedsMesh: { [id: string]: { patientMesh: StaticMesh; bedMesh: StaticMesh } } = {};
    consoleMesh!: Mesh;
    trocarMesh!: Mesh;
    iBsuMesh!: Mesh;
    vBsuMesh!: Mesh;
    loadedAssets: { [id: string]: { obj: StaticMesh | Robot } } = {};
    _modelData: IDataModel;
    assetsloaded = false;
    /**
     * Handle loading assets
     */
    constructor(private scene: Scene) {
        BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
        BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
        Mesh.prototype.raycast = acceleratedRaycast;

        this.loadingMan = new LoadingManager(this.onLoad, this.onProgress, this.onError);

        this.gltfLoader = new GLTFLoader(this.loadingMan);

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath("/draco/");
        this.gltfLoader.setDRACOLoader(dracoLoader);
        this.envMapLoader = new CubeTextureLoader(this.loadingMan);
        this.texLoader = new TextureLoader();
        this._modelData = modelsData.globalSettings;
        this.loadEnvMap(this._modelData.Paths.envMapFolder);

        Object.keys(this._modelData.settings.BSUColors).forEach((key) => {
            const hexcolor = (this._modelData.settings.BSUColors as { [id: string]: string })[key];
            Inventory.colors[key] = {
                name: key,
                hexColor: hexcolor,
                isUsed: false,
            };
        });
        this.loadAssets();
    }

    loadAssets(): void {
        //load ibsu
        const item = this._modelData.models.iBSU;
        let url = this.getItemUrl(item.mesh.filename, item.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const bsuProp = this.processBSUModel(gltf.scene as Group, item);
            if (bsuProp) {
                Inventory.iBsu = new Robot(bsuProp.arm, bsuProp.effector, bsuProp.root);
                Inventory.iBsu.setBSUMesh(bsuProp.baseMesh, bsuProp.armMesh);
                Inventory.iBsu.group = bsuProp.group;
                Inventory.iBsu.setRotationY(90, undefined);
                Inventory.iBsu.name = item.name;
            }
        });

        //load vbsu
        const item1 = this._modelData.models.vBSU;
        const url1 = this.getItemUrl(item1.mesh.filename, item1.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url1, (gltf: any) => {
            const bsuProp = this.processBSUModel(gltf.scene as Group, item1);
            if (bsuProp) {
                Inventory.vBsu = new Robot(bsuProp.arm, bsuProp.effector, bsuProp.root);
                Inventory.vBsu.setBSUMesh(bsuProp.baseMesh, bsuProp.armMesh);
                Inventory.vBsu.group = bsuProp.group;
                Inventory.vBsu.name = item1.name;
                Inventory.vBsu.setRotationY(90, undefined);
                Inventory.vBsu.setLight(Inventory.colors["white"]);
            }
        });

        //load console
        const consol = this._modelData.models.console;
        url = this.getItemUrl(consol.mesh.filename, consol.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const meshes: Mesh[] = [];
            const scale = new Vector3(1.0, 1.0, 1.0);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            gltf.scene.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    _child.castShadow = true;
                    _child.receiveShadow = true;
                    _child.frustumCulled = false;
                    meshes.push(_child);
                    if (_child.parent) {
                        scale.copy(_child.parent.scale);
                    }
                }
            });

            Inventory.SM_console = new StaticMesh(AssetType.Console, 0.0);
            meshes.forEach((_child) => {
                const mat = _child.material as MeshStandardMaterial;
                if (mat.map) {
                    mat.map.colorSpace = SRGBColorSpace;
                    mat.map.anisotropy = 4;
                }
                Inventory.SM_console.addMesh(_child);
            });
            Inventory.SM_console.name = consol.name;
            Inventory.SM_console.group.scale.copy(scale);

            Inventory.SM_console.addCollisionEnvelop(
                this.computeCollision(Inventory.SM_console.group)
            );
        });

        this.loadFlattenedStaticModel(
            this._modelData.models.trolley,
            AssetType.Trolley,
            (mesh) => {
                Inventory.SM_trolley = mesh;
            },
            0.02
        );

        this.loadFlattenedStaticModel(
            this._modelData.models.surgeon,
            AssetType.Surgeon,
            (mesh) => {
                Inventory.SM_surgeon = mesh;
            },
            0.02,
            0.8
        );

        this.loadFlattenedStaticModel(
            this._modelData.models.slave,
            AssetType.Slave,
            (mesh) => {
                Inventory.SM_slave = mesh;
            },
            0.02
        );

        this.loadFlattenedStaticModel(
            this._modelData.models.anesthesia,
            AssetType.Anesthesia,
            (mesh) => {
                Inventory.SM_anesthesia = mesh;
            },
            0.02
        );

        //load stack
        const stack = this._modelData.models.stack;
        url = this.getItemUrl(stack.mesh.filename, stack.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const meshes: Mesh[] = [];
            const scale = new Vector3(1.0, 1.0, 1.0);
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            gltf.scene.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    _child.castShadow = true;
                    const mat = _child.material as MeshStandardMaterial;
                    this.loadEnvMap(this._modelData.Paths.envMapFolder, (texture) => {
                        mat.envMap = texture;
                        mat.envMap.needsUpdate = true;
                    });

                    if (mat.map) mat.map.anisotropy = 4;
                    meshes.push(_child);
                    if (_child.parent) {
                        scale.copy(_child.parent.scale);
                    }
                }
            });
            Inventory.SM_stack = new StaticMesh(AssetType.Stack, 0.02);
            meshes.forEach((_child) => {
                Inventory.SM_stack.addMesh(_child);
            });
            Inventory.SM_stack.name = stack.name;
            Inventory.SM_stack.group.scale.copy(scale);
            Inventory.SM_stack.addCollisionEnvelop(this.computeCollision(Inventory.SM_stack.group));
        });

        //load trocar
        const trocar = this._modelData.models.trocar;
        url = this.getItemUrl(trocar.mesh.filename, trocar.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const meshes: Mesh[] = [];
            const scale = new Vector3(1.0, 1.0, 1.0);
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            gltf.scene.traverse((_child: any) => {
                _child.castShadow = true;
                if (_child instanceof Mesh) {
                    meshes.push(_child);
                    if (_child.parent) {
                        scale.copy(_child.parent.scale);
                    }
                }
            });

            Inventory.SM_trocar = new StaticMesh(AssetType.Trocar);
            meshes.forEach((_child) => {
                Inventory.M_trocar = _child.material as MeshStandardMaterial;
                if (Inventory.M_trocar.map) Inventory.M_trocar.map.anisotropy = 8;
                this.loadEnvMap(this._modelData.Paths.envMapFolder, (tex) => {
                    Inventory.M_trocar.envMap = tex;
                    Inventory.M_trocar.envMap.needsUpdate = true;
                    Inventory.M_trocar.envMapIntensity = 3.0;
                });

                Inventory.SM_trocar.addMesh(_child);
            });
            Inventory.SM_trocar.group.scale.copy(scale);
            //Inventory.SM_trocar.group.rotateX(Math.PI / 2);
            Inventory.SM_trocar.group.updateMatrixWorld(true);
            Inventory.SM_trocar.name = trocar.name;
        });

        //load Patient Bed model
        this._modelData.models.patientBed.forEach((item) => {
            const fileName = item.mesh.filename;

            const url = [
                this._modelData.Paths.ModelPath,
                fileName,
                fileName + item.mesh.extention,
            ].join("/");

            const id = item.name;

            Inventory.SM_PatientBed[id] = {
                SM_Patient: new StaticMesh(AssetType.Patient),
                SM_Bed: new StaticMesh(AssetType.Bed),
            };

            this.loadModel(url, (gltf: any) => {
                if (!gltf || !gltf.scene) {
                    console.error("Invalid patient GLTF:", id);
                    return;
                }

                const root = gltf.scene as Group;

                const bedName = item.mesh.bed;
                const zoneMeshName = item.mesh.zone;
                const dropzoneMeshName = item.mesh.dropzone;

                this.setBedColour(root);

                const bedMeshes: Mesh[] = [];

                let patientFound = false;

                const patientSM = Inventory.SM_PatientBed[id].SM_Patient;
                const bedSM = Inventory.SM_PatientBed[id].SM_Bed;

                /*
                 * Store transform nodes
                 */
                patientSM.nodes = {};
                patientSM.transforms = {};

                bedSM.nodes = {};
                bedSM.transforms = {};

                root.traverse((child: any) => {
                    if (child.name) {
                        const nodeName = child.name.toLowerCase();

                        patientSM.nodes[nodeName] = child;

                        patientSM.transforms[nodeName] = {
                            object: child,
                            position: child.position.clone(),
                            rotation: child.rotation.clone(),
                            scale: child.scale.clone(),
                        };

                        child.userData.originalTransform = {
                            position: child.position.clone(),
                            rotation: child.rotation.clone(),
                            scale: child.scale.clone(),
                        };
                    }

                    /*
                     * Patient mesh
                     */
                    if (child.isSkinnedMesh) {
                        patientFound = true;

                        child.castShadow = true;
                        child.receiveShadow = true;
                        child.frustumCulled = false;

                        this.fixGLTFMesh(child);

                        const materials = Array.isArray(child.material)
                            ? child.material
                            : [child.material];

                        materials.forEach((mat: MeshStandardMaterial) => {
                            if (mat.map) {
                                mat.map.colorSpace = SRGBColorSpace;
                                mat.map.anisotropy = 8;
                            }

                            mat.side = DoubleSide;
                            mat.roughness = 0.75;
                            mat.metalness = 0;
                            mat.needsUpdate = true;
                        });
                    } else if (child.isMesh) {
                        /*
                         * Bed meshes
                         */
                        const isBed =
                            child.name.includes(bedName) ||
                            (item.mesh.extraBedMeshes ?? []).includes(child.name);

                        if (isBed) {
                            child.castShadow = true;
                            child.receiveShadow = true;
                            child.frustumCulled = false;

                            child.geometry?.computeBoundsTree();

                            bedMeshes.push(child);

                            const nodeName = child.name.toLowerCase();

                            bedSM.nodes[nodeName] = child;

                            bedSM.transforms[nodeName] = {
                                object: child,
                                position: child.position.clone(),
                                rotation: child.rotation.clone(),
                                scale: child.scale.clone(),
                            };
                        } else {
                            this.fixGLTFMesh(child);
                        }
                    }
                });

                /*
                 * Patient setup
                 */
                if (patientFound) {
                    root.updateMatrixWorld(true);

                    patientSM.group.add(root);

                    patientSM.name = item.name;

                    root.traverse((child: any) => {
                        if (child.isMesh || child.isSkinnedMesh) {
                            child.userData = {
                                ...child.userData,
                                type: AssetType.Patient,
                                staticmesh: patientSM,
                            };

                            patientSM.meshes.push(child);
                        }
                    });
                } else {
                    console.error("No skinned patient mesh found in GLTF:", id);
                }

                /*
                 * Bed setup
                 */
                bedMeshes.forEach((mesh) => {
                    if (!mesh.geometry.boundsTree) {
                        mesh.geometry.computeBoundsTree();
                    }

                    mesh.userData = {
                        ...mesh.userData,
                        type: AssetType.Bed,
                        staticmesh: bedSM,
                        patientBed: Inventory.SM_PatientBed[id],
                    };

                    bedSM.meshes.push(mesh);
                });

                bedSM.name = item.name;

                if (bedMeshes.length > 0) {
                    root.updateMatrixWorld(true);

                    const collisionMesh = this.computeCollisionFromMeshes(bedMeshes);

                    bedSM.addCollisionEnvelop(collisionMesh);
                }

                /*
                 * Zones
                 */
                root.traverse((child: any) => {
                    if (!child.isMesh) return;

                    if (zoneMeshName && child.name.includes(zoneMeshName)) {
                        child.material = new MeshStandardMaterial();

                        child.visible = false;

                        patientSM.group.userData.zone = child;
                    }

                    if (dropzoneMeshName && child.name.includes(dropzoneMeshName)) {
                        child.material = new MeshStandardMaterial({
                            color: "#19ddf7",
                            opacity: 0,
                            transparent: true,
                        });

                        child.visible = true;

                        patientSM.group.userData.dropzone = child;
                    }
                });

                patientSM.group.updateMatrixWorld(true);
                bedSM.group.updateMatrixWorld(true);
            });
        });
    }
    /**
     * Load a GLTF model
     * @param url Path to the .gltf file
     * @param onLoaded executed when the file loaded
     */
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    public loadModel(url: string, onLoaded: (gltf: any) => void): void {
        this.gltfLoader?.load(url, onLoaded, null, null);
    }

    /**
     * Load a cube texture used as an environment map
     * @param path path to the folder containing the textures
     * @param ext type of the files (png, jpg, etc)
     * @returns
     */
    public loadEnvMap(
        path: string,
        onLoaded: (texture: CubeTexture) => void = () => {
            return;
        },
        ext = "jpg"
    ): CubeTexture {
        this.envMapLoader.setPath(path);
        const envMap = this.envMapLoader.load(
            ["px." + ext, "nx." + ext, "py." + ext, "ny." + ext, "pz." + ext, "nz." + ext],
            (texture) => {
                Inventory.T_envMap = texture;
                Inventory.T_envMap.needsUpdate = true;
                onLoaded(texture);
            }
        );
        return envMap;
    }
    public loadTexture(
        url: string,
        onReady: (tex: Texture) => void = function () {
            return;
        }
    ): Texture {
        return this.texLoader.load(url, onReady);
    }
    private onLoad(): void {
        dataEvents.emit("assetsLoaded");
    }

    private onProgress(url: string, loaded: number, total: number): void {
        dataEvents.emit("loading", loaded, total);
    }

    private onError(url: string): void {
        console.error("error loading : ", url);
        dataEvents.emit("errorLoading", url);
    }
    private getItemUrl(foldername: string, item: { filename: string; extention: string }): string {
        return [this._modelData.Paths.ModelPath, foldername, item.filename + item.extention].join(
            "/"
        );
    }
    /**
     * Load a scene configuration from a json file
     * @param url path to the json file
     * @returns
     */

    static loadSceneConfiguration(key: string): ISceneConfiguration {
        // load scene configuration
        const sceneConf = rooms.preBuiltScenes as { [key: string]: ISceneConfiguration };
        return sceneConf[key];
    }
    computeCollision(group: Group): Mesh {
        const box = new Box3().setFromObject(group);

        const size = new Vector3();
        const center = new Vector3();

        box.getSize(size);
        box.getCenter(center);
        const meshCollision = new Mesh(new BoxGeometry(size.x, size.y, size.z));
        meshCollision.position.copy(center);
        meshCollision.updateMatrix();
        meshCollision.geometry.userData.obb = new OBB();
        meshCollision.geometry.userData.obb.halfSize.copy(size).multiplyScalar(0.5);
        meshCollision.userData = { obb: new OBB() };
        return meshCollision;
    }

    private computeCollisionFromMeshes(meshes: Mesh[]): Mesh {
        const box = new Box3();

        meshes.forEach((mesh) => {
            mesh.updateWorldMatrix(true, false);

            const meshBox = new Box3().setFromObject(mesh);
            box.union(meshBox);
        });

        const size = new Vector3();
        const center = new Vector3();

        box.getSize(size);
        box.getCenter(center);

        const collisionMesh = new Mesh(new BoxGeometry(size.x, size.y, size.z));

        collisionMesh.position.copy(center);
        collisionMesh.updateMatrix();
        collisionMesh.updateMatrixWorld(true);

        collisionMesh.geometry.computeBoundingBox();

        collisionMesh.geometry.userData.obb = new OBB();
        collisionMesh.geometry.userData.obb.halfSize.copy(size).multiplyScalar(0.5);

        collisionMesh.userData = {
            obb: new OBB(),
        };

        return collisionMesh;
    }

    processBSUModel(
        gltf: Group,
        item: { mesh: { meshName: { base: string; arm: string } } }
    ):
        | {
              arm: Bone[];
              effector: Bone;
              root: Bone;
              baseMesh: Mesh;
              armMesh: Mesh;
              group: Group;
          }
        | undefined {
        const bones: Bone[] = [];
        const arm: Bone[] = [];
        const group: Group = gltf;
        const effector: Bone = new Bone();
        const meshes: { [id: string]: Mesh } = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        gltf.traverse((_child: any) => {
            if (_child instanceof Mesh) {
                _child.frustumCulled = false;
                _child.castShadow = true;
                const mat = _child.material as MeshStandardMaterial;
                if (mat.map) {
                    mat.map.anisotropy = 16;
                    mat.map.magFilter = NearestFilter;
                    mat.map.minFilter = NearestFilter;
                    mat.needsUpdate = true;
                }
                if (
                    _child.name === item.mesh.meshName.base ||
                    _child.name === item.mesh.meshName.arm
                ) {
                    meshes[_child.name] = _child;
                }
            }
            if (_child instanceof Bone) {
                bones.push(_child);
            }
        });
        if (bones.length > 0) {
            arm.push(bones[3]);
            for (let i = 3; i < 12; i++) {
                arm.push(bones[i]);
            }
            const n = arm.length;
            arm[2].userData["jointLimits"] = {
                qmin: -0.47 * Math.PI,
                qmax: 0.47 * Math.PI,
            };
            arm[4].userData["jointLimits"] = { qmin: 0.0, qmax: 0.95 * Math.PI };
            arm[n - 4].userData["jointLimits"] = { qmin: 0, qmax: Math.PI };
            arm[n - 3].userData["jointLimits"] = { qmin: -Math.PI, qmax: 0.0 };
            arm[n - 1].add(effector);
            effector.matrixWorld = arm[n - 1].matrixWorld.clone();
            effector.translateZ(0.25);
            effector.translateY(0.012);
            effector.updateMatrixWorld();
            const res = {
                arm: arm,
                effector: effector,
                root: bones[0],
                baseMesh: meshes[item.mesh.meshName.base],
                armMesh: meshes[item.mesh.meshName.arm],
                group: group,
            };

            return res;
        }
        return;
    }

    processCollisionMesh(_child: Mesh, collisionEnvelop: { obb: Mesh[]; sphere: Mesh[] }): void {
        if (_child.name.startsWith("CSM")) {
            const geom = _child.geometry as BufferGeometry;
            geom.computeBoundingSphere();
            collisionEnvelop.sphere.push(_child);
        } else if (_child.name.startsWith("CBM")) {
            const rotM = _child.matrixWorld.clone();
            const q = new Quaternion();
            _child.matrixWorld.decompose(new Vector3(), q, new Vector3());
            const iq = q.clone().invert();
            _child.applyQuaternion(iq);
            const geom = _child.geometry as BufferGeometry;
            geom.computeBoundingBox();
            const size = new Vector3();
            if (geom.boundingBox != null) {
                geom.boundingBox.getSize(size);
                _child.geometry.userData["obb"] = new OBB();
                _child.geometry.userData.obb.halfSize.copy(size).multiplyScalar(0.5);
                _child.applyMatrix4(rotM);
                _child.userData["obb"] = new OBB();
            }
            collisionEnvelop.obb.push(_child);
        }
    }

    processPorts(child: Mesh): { position: Vector3; matrix: Matrix4 } {
        child.updateMatrix();
        child.updateMatrixWorld();
        return { position: child.position, matrix: child.matrixWorld };
    }

    private loadRiggedStaticModel(
        item: {
            name: string;
            mesh: {
                filename: string;
                extention: string;
            };
        },
        assetType: AssetType,
        targetSetter: (mesh: StaticMesh) => void,
        collisionOffset = 0.02
    ): void {
        const url = this.getItemUrl(item.mesh.filename, item.mesh);

        this.loadModel(url, (gltf: any) => {
            const root = gltf.scene as Group;

            if (!root) {
                console.error("Rigged model failed to load:", item.name);
                return;
            }

            root.updateMatrixWorld(true);

            const staticMesh = new StaticMesh(assetType, collisionOffset);
            staticMesh.name = item.name;
            staticMesh.meshes = [];

            root.traverse((child: any) => {
                if (child.isMesh || child.isSkinnedMesh) {
                    child.visible = true;
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.frustumCulled = false;
                    child.userData = { type: assetType, staticmesh: staticMesh };

                    if (Array.isArray(child.material)) {
                        child.material.forEach((mat: MeshStandardMaterial) => {
                            if (mat.map) {
                                mat.map.colorSpace = SRGBColorSpace;
                                mat.map.anisotropy = 4;
                            }
                            mat.needsUpdate = true;
                        });
                    } else if (child.material) {
                        const mat = child.material as MeshStandardMaterial;
                        if (mat.map) {
                            mat.map.colorSpace = SRGBColorSpace;
                            mat.map.anisotropy = 4;
                        }
                        mat.needsUpdate = true;
                    }

                    staticMesh.meshes.push(child as Mesh);
                }
            });

            root.updateMatrixWorld(true);

            const box = new Box3().setFromObject(root);
            const center = new Vector3();
            const size = new Vector3();
            box.getCenter(center);
            box.getSize(size);

            root.position.sub(center);
            root.position.y += size.y / 2;

            const maxDim = Math.max(size.x, size.y, size.z);
            const scaleFix = maxDim > 0 ? 2 / maxDim : 1;
            root.scale.setScalar(scaleFix);

            // Keep the rig/skeleton as a child. StaticMesh.setPosition() moves this wrapper,
            // so it will not overwrite the rig's local centering/scale fix.
            staticMesh.group.add(root);
            staticMesh.group.updateMatrixWorld(true);

            staticMesh.addCollisionEnvelop(this.computeCollision(staticMesh.group));

            targetSetter(staticMesh);
        });
    }

    private loadFlattenedStaticModel(
        item: {
            name: string;
            mesh: {
                filename: string;
                extention: string;
            };
            exclude?: string[];
        },
        assetType: AssetType,
        targetSetter: (mesh: StaticMesh) => void,
        collisionOffset = 0.02,
        modelScale = 1
    ): void {
        const url = this.getItemUrl(item.mesh.filename, item.mesh);

        this.loadModel(url, (gltf: any) => {
            const root = gltf.scene as Group;

            root.updateMatrixWorld(true);

            const staticMesh = new StaticMesh(assetType, collisionOffset);

            const fixMaterial = (material: MeshStandardMaterial): MeshStandardMaterial => {
                if (Inventory.T_envMap) {
                    material.envMap = Inventory.T_envMap;
                    material.envMapIntensity = 0.6;
                }

                if (material.map) {
                    material.map.colorSpace = SRGBColorSpace;
                    material.map.anisotropy = 4;
                }

                material.needsUpdate = true;

                return material;
            };

            const excludedMeshes = item.exclude ?? [];

            root.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    if (excludedMeshes.includes(_child.name)) {
                        return;
                    }

                    const mesh = _child.clone(false);

                    mesh.geometry = _child.geometry.clone();
                    mesh.geometry.applyMatrix4(_child.matrixWorld);

                    mesh.position.set(0, 0, 0);
                    mesh.rotation.set(0, 0, 0);
                    mesh.quaternion.identity();
                    mesh.scale.set(1, 1, 1);
                    mesh.updateMatrixWorld(true);

                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    mesh.frustumCulled = false;

                    if (Array.isArray(_child.material)) {
                        mesh.material = _child.material.map((m: MeshStandardMaterial) =>
                            fixMaterial(m.clone() as MeshStandardMaterial)
                        );
                    } else {
                        mesh.material = fixMaterial(
                            (_child.material as MeshStandardMaterial).clone()
                        );
                    }

                    staticMesh.addMesh(mesh);
                }
            });

            staticMesh.name = item.name;
            staticMesh.group.updateMatrixWorld(true);

            const box = new Box3().setFromObject(staticMesh.group);
            const yOffset = -box.min.y;

            staticMesh.group.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    _child.geometry.translate(0, yOffset, 0);

                    if (modelScale !== 1) {
                        _child.geometry.scale(modelScale, modelScale, modelScale);
                    }

                    _child.geometry.computeBoundingBox();
                    _child.geometry.computeBoundingSphere();
                }
            });

            staticMesh.group.updateMatrixWorld(true);

            staticMesh.addCollisionEnvelop(this.computeCollision(staticMesh.group));

            targetSetter(staticMesh);
        });
    }

    public loadRiggedModel(
        item: { name: string; mesh: { filename: string; extention: string } },
        onLoaded: (rigged: RiggedMesh) => void
    ): void {
        const url = this.getItemUrl(item.mesh.filename, item.mesh);

        this.loadModel(url, (gltf: any) => {
            const group = gltf.scene as Group;

            if (!group) {
                console.error("Rigged model failed to load:", item.name);
                return;
            }

            const bones: Bone[] = [];

            group.traverse((child: any) => {
                if (child.isSkinnedMesh && child.skeleton) {
                    bones.push(...child.skeleton.bones);
                }

                if (child.isMesh || child.isSkinnedMesh) {
                    child.visible = true;
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.frustumCulled = false;

                    if (Array.isArray(child.material)) {
                        child.material.forEach((mat: MeshStandardMaterial) => {
                            mat.needsUpdate = true;
                        });
                    } else if (child.material) {
                        (child.material as MeshStandardMaterial).needsUpdate = true;
                    }
                }
            });

            group.updateMatrixWorld(true);

            const box = new Box3().setFromObject(group);
            const center = new Vector3();
            const size = new Vector3();

            box.getCenter(center);
            box.getSize(size);

            group.position.sub(center);

            group.position.y += size.y / 2;

            const maxDim = Math.max(size.x, size.y, size.z);
            const scaleFix = maxDim > 0 ? 2 / maxDim : 1;
            group.scale.setScalar(scaleFix);

            group.updateMatrixWorld(true);

            console.log("Rigged loaded:", item.name, {
                bones: bones.length,
                hasScene: !!group,
                size,
                scaleFix,
            });

            onLoaded(new RiggedMesh(group, bones));
        });
    }

    public addRiggedModelToScene(
        rigged: RiggedMesh | null,
        position: Vector3 = new Vector3(0, 0, 0),
        rotationY = 0
    ): Group | null {
        if (!rigged) {
            console.warn("Tried to add rigged model before it was loaded");
            return null;
        }

        const clone = (SkeletonUtils as any).clone(rigged.group) as Group;

        clone.position.copy(position);
        clone.rotation.y = rotationY;
        clone.visible = true;

        clone.traverse((child: any) => {
            if (child.isMesh || child.isSkinnedMesh) {
                child.visible = true;
                child.castShadow = true;
                child.receiveShadow = true;
                child.frustumCulled = child.isSkinnedMesh ? false : true;
            }
        });

        clone.updateMatrixWorld(true);
        this.scene.add(clone);

        console.log("Rigged model clone added to scene:", clone);

        return clone;
    }

    public addSurgeonToScene(
        position: Vector3 = new Vector3(0, 0, 0),
        rotationY = 0
    ): Group | null {
        return this.addRiggedModelToScene(RiggedInventory.surgeon, position, rotationY);
    }

    private fixGLTFMaterial(material: MeshStandardMaterial): void {
        if (Inventory.T_envMap) {
            material.envMap = Inventory.T_envMap;
            material.envMapIntensity = 1.5;
        }

        material.metalness = Math.min(material.metalness, 0.6);
        material.roughness = Math.max(material.roughness, 0.4);

        if (material.map) {
            material.map.colorSpace = SRGBColorSpace;
            material.map.anisotropy = 4;
        }

        material.needsUpdate = true;
    }

    private fixGLTFMesh(mesh: Mesh): void {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.frustumCulled = false;

        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material) => {
                this.fixGLTFMaterial(material as MeshStandardMaterial);
            });
        } else {
            this.fixGLTFMaterial(mesh.material as MeshStandardMaterial);
        }
    }

    /**
     * Sets the colour of the bed
     *
     * @param patientRoot
     */
    private setBedColour(patientRoot: Group): void {
        patientRoot.traverse((child: any) => {
            if (!child.isSkinnedMesh) {
                return;
            }

            const recolourMaterial = (
                material: MeshStandardMaterial,
                colour
            ): MeshStandardMaterial => {
                const patientMaterial = material.clone();

                patientMaterial.color.set(colour);
                patientMaterial.map = null;

                if (patientMaterial.emissive) {
                    patientMaterial.emissive.set(0x000000);
                }

                patientMaterial.vertexColors = false;
                patientMaterial.roughness = 0.75;
                patientMaterial.metalness = 0;
                patientMaterial.needsUpdate = true;

                return patientMaterial;
            };

            if (child.name === "Patient") {
                child.material = recolourMaterial(
                    child.material as MeshStandardMaterial,
                    PATIENT_COLOUR
                );
                return;
            }

            if (child.name === "Bed-White_Plastic") {
                child.material = recolourMaterial(
                    child.material as MeshStandardMaterial,
                    BED_COLOUR
                );
                return;
            }
        });
    }
}
